import type { UtopiaAuthUser } from '../react/utopia/utopiaAuthState'

export type AuthSessionResponse = {
  user: UtopiaAuthUser
  expiresAt?: number
  error?: string
}

export const MOJANG_USERNAME_ERROR_CODE = 'mojang_username'

export class AuthApiError extends Error {
  code?: string

  constructor (message: string, code?: string) {
    super(message)
    this.name = 'AuthApiError'
    this.code = code
  }
}

const authCredentials: RequestCredentials = 'include'

async function authFetch<T> (path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: authCredentials,
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })
  const data = await res.json() as T & { error?: string, code?: string }
  if (!res.ok) {
    throw new AuthApiError(data.error ?? res.statusText, data.code)
  }
  return data
}

export type UtopiaUsernameCheckResponse = {
  exists: boolean
  username?: string
  error?: string
}

export async function checkUtopiaUsernameExists (username: string) {
  const res = await fetch(
    `/api/auth/check-username?username=${encodeURIComponent(username.trim())}`,
  )
  const data = await res.json() as UtopiaUsernameCheckResponse
  if (!res.ok) {
    throw new Error(data.error ?? res.statusText)
  }
  return data
}

export type MicrosoftProofResponse = {
  proofToken: string
  profileName: string
  profileId: string
  expiresAt: number
}

export async function exchangeMicrosoftProof (accessToken: string) {
  return authFetch<MicrosoftProofResponse>('/api/auth/microsoft/verify', {
    method: 'POST',
    body: JSON.stringify({ accessToken }),
  })
}

/** IPv4 publique du navigateur (api.ipify.org). */
export async function fetchPublicIpv4 (): Promise<string | null> {
  try {
    const res = await fetch('https://api.ipify.org?format=json')
    const data = await res.json() as { ip?: string }
    if (typeof data.ip === 'string' && data.ip.trim()) return data.ip.trim()
  } catch {
    /* ignore */
  }
  return null
}

export async function loginAuth (
  username: string,
  password: string,
  microsoftProofToken?: string,
) {
  const clientPublicIpv4 = await fetchPublicIpv4()
  return authFetch<AuthSessionResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
      microsoftProofToken,
      clientPublicIpv4: clientPublicIpv4 ?? undefined,
    }),
  })
}

export type BedrockConsolePreviewResponse = {
  ok: boolean
  username?: string
  platform?: string
  platformId?: string
  detectionSource?: string
  expiresAt?: number
  error?: string
}

export async function previewBedrockConsoleRegistration (code: string) {
  const trimmed = code.trim().toUpperCase()
  const clientPublicIpv4 = await fetchPublicIpv4()
  const params = new URLSearchParams({ code: trimmed })
  if (clientPublicIpv4) params.set('clientPublicIpv4', clientPublicIpv4)
  const res = await fetch(
    `/api/auth/bedrock-console/preview?${params.toString()}`,
    { credentials: authCredentials },
  )
  const data = await res.json() as BedrockConsolePreviewResponse
  if (!res.ok) {
    throw new AuthApiError(data.error ?? res.statusText)
  }
  return data
}

export async function registerAuth (
  username: string,
  password: string,
  email?: string,
  microsoftProofToken?: string,
  bedrockConsoleCode?: string,
) {
  const clientPublicIpv4 = await fetchPublicIpv4()
  if (!clientPublicIpv4) {
    throw new Error(
      'Impossible de récupérer votre IP publique pour l\'inscription. '
      + 'Vérifiez votre connexion ou réessayez.',
    )
  }
  return authFetch<AuthSessionResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
      email,
      microsoftProofToken,
      bedrockConsoleCode: bedrockConsoleCode?.trim().toUpperCase() || undefined,
      clientPublicIpv4,
    }),
  })
}

export async function fetchAuthMe (): Promise<UtopiaAuthUser | null> {
  try {
    const data = await authFetch<{ user: UtopiaAuthUser }>('/api/auth/me')
    return data.user
  } catch {
    return null
  }
}

export async function logoutAuth () {
  try {
    await authFetch<{ ok: boolean }>('/api/auth/logout', { method: 'POST' })
  } catch {
    /* ignore */
  }
}

export async function changeAuthPassword (
  currentPassword: string,
  newPassword: string,
) {
  return authFetch<{ ok?: boolean, user?: UtopiaAuthUser }>('/api/auth/password', {
    method: 'PATCH',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

export async function changeAuthEmail (password: string, email: string) {
  return authFetch<{ ok: boolean, user: UtopiaAuthUser }>('/api/auth/email', {
    method: 'PATCH',
    body: JSON.stringify({ password, email }),
  })
}

export async function deleteAuthAccount (password: string) {
  return authFetch<{ ok: boolean }>('/api/auth/account', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  })
}

export type IpGameSessionStatus = {
  active: boolean
  expired?: boolean
  ipMasked?: string | null
  requestIpMasked?: string | null
  timeoutMinutes?: number
  expiresAt?: number
  error?: string
}

export type IpGameSessionActivateResponse = IpGameSessionStatus & {
  ok: boolean
}

/** État session IP en base (persistant après rafraîchissement). */
export async function fetchIpGameSession (): Promise<IpGameSessionStatus> {
  const clientPublicIpv4 = await fetchPublicIpv4()
  const qs = clientPublicIpv4
    ? `?clientPublicIpv4=${encodeURIComponent(clientPublicIpv4)}`
    : ''
  return authFetch<IpGameSessionStatus>(`/api/auth/ip-session${qs}`)
}

/** Active ou met à jour la session IP jeu (reconnexion sans /login si même réseau). */
export async function activateIpGameSession () {
  const clientPublicIpv4 = await fetchPublicIpv4()
  if (!clientPublicIpv4) {
    throw new Error(
      'Impossible de récupérer votre IP publique. Vérifiez votre connexion ou réessayez.',
    )
  }
  return authFetch<IpGameSessionActivateResponse>('/api/auth/ip-session', {
    method: 'POST',
    body: JSON.stringify({ clientPublicIpv4 }),
  })
}
